import React from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "@/context/AuthContext";
import usePageMeta from "@/lib/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ");
  return String(detail);
};

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [password, setPassword] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState(false);

  usePageMeta({
    title: "Choose a new password | CV AI PRO",
    description: "Set a new password.",
    path: "/reset-password",
  });

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password too short", { description: "Use at least 8 characters." });
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match", { description: "Type the same password twice." });
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/auth/reset-password`,
        { token, password },
        { withCredentials: true }
      );
      setDone(true);
      refresh();
      setTimeout(() => navigate("/account", { replace: true }), 1600);
    } catch (err) {
      toast.error("Couldn't reset password", {
        description: formatError(err?.response?.data?.detail),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-black text-white" data-testid="reset-password-page">
      <div aria-hidden className="absolute -top-32 left-1/2 -translate-x-1/2 h-[520px] w-[520px] rounded-full bg-blue-600/15 blur-[140px]" />
      <div aria-hidden className="absolute inset-0 grid-bg" />

      <div className="relative mx-auto max-w-md px-4 sm:px-6 pt-32 sm:pt-40 pb-24">
        {done ? (
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="text-center" data-testid="reset-done">
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15 border border-emerald-500/30">
              <CheckCircle2 className="h-8 w-8 text-emerald-400" />
            </div>
            <h1 className="mt-8 text-3xl sm:text-4xl font-semibold tracking-tighter text-white text-balance">
              Password updated.
            </h1>
            <p className="mt-5 text-zinc-400">You're signed in — taking you to your account…</p>
          </motion.div>
        ) : !token ? (
          <div className="text-center" data-testid="reset-missing-token">
            <h1 className="text-3xl font-semibold tracking-tighter text-white">Missing reset token.</h1>
            <p className="mt-4 text-zinc-400">Use the link from your reset email, or request a new one.</p>
            <Link
              to="/forgot-password"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-black px-6 py-3 text-sm font-medium hover:bg-zinc-200 transition-colors"
              data-testid="reset-request-new"
            >
              Request new link
            </Link>
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-white/5 border border-white/10">
              <Lock className="h-6 w-6 text-zinc-300" />
            </div>
            <h1 className="mt-8 text-3xl sm:text-4xl font-semibold tracking-tighter text-white text-balance" data-testid="reset-headline">
              Choose a new password.
            </h1>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <Label htmlFor="new-password" className="text-xs text-zinc-400">New password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                  minLength={8}
                  className="mt-1.5 bg-black/40 border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-white/30"
                  data-testid="reset-input-password"
                />
              </div>
              <div>
                <Label htmlFor="confirm-password" className="text-xs text-zinc-400">Confirm password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Repeat it"
                  required
                  minLength={8}
                  className="mt-1.5 bg-black/40 border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-white/30"
                  data-testid="reset-input-confirm"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-black px-6 py-3.5 text-sm font-medium hover:bg-zinc-200 transition-colors disabled:opacity-60"
                data-testid="reset-submit"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                Update password
              </button>
            </form>
          </motion.div>
        )}
      </div>
    </div>
  );
}
